import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { ProposalCard } from "@/components/ProposalCard";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export default function Approvals() {
  const [status, setStatus] = useState("pending");
  const [module, setModule] = useState("all");
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/proposals", { params: { status } }); setItems(data); } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const update = (p) => setItems((xs) => xs.map((x) => (x.action_id === p.action_id ? p : x)));
  const list = (items || []).filter((p) => module === "all" || p.module === module);

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" subtitle="Every action your AI team has proposed, with a full record of decisions"
        actions={<>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 bg-card" data-testid="approvals-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="executed">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger className="w-40 bg-card" data-testid="approvals-filter-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              <SelectItem value="calendar">Calendar</SelectItem>
              <SelectItem value="tasks">Tasks</SelectItem>
              <SelectItem value="goals">Goals</SelectItem>
              <SelectItem value="hr">HR</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="marketing">Marketing</SelectItem>
              <SelectItem value="seo">SEO</SelectItem>
            </SelectContent>
          </Select>
        </>} />
      {items === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        list.length === 0 ? <div className="bg-card border border-border rounded-xl"><EmptyState icon={ShieldCheck} title="Nothing here" text="Ask the AI CEO to do something. Its proposals will appear here for review." /></div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="approvals-table">
            {list.map((p) => (
              <div key={p.action_id} data-testid="approvals-row">
                <ProposalCard proposal={p} onChange={update} />
                <p className="text-[11px] text-muted-foreground mt-1.5 px-1">Requested by {p.created_by_name} · {fmtDateTime(p.created_at)}</p>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
